import { useEffect, useRef } from "react";

/**
 * Reacts to the browser's geolocation permission changing while the page is open.
 *
 * - onGranted: permission became "granted", or (retryOnFocus) the user came back
 *   to the tab, e.g. after changing the site setting.
 * - onDenied: permission became "denied".
 *
 * Browsers without the Permissions API (older Safari) rely on the focus retry.
 */
const useGeolocationPermission = ({ onGranted, onDenied, retryOnFocus = false }) => {
  const callbacksRef = useRef({ onGranted, onDenied });
  callbacksRef.current = { onGranted, onDenied };

  useEffect(() => {
    if (!navigator.permissions?.query) return undefined;

    let status = null;
    let cancelled = false;

    const handleChange = () => {
      if (!status) return;
      if (status.state === "granted") callbacksRef.current.onGranted?.();
      else if (status.state === "denied") callbacksRef.current.onDenied?.();
    };

    navigator.permissions
      .query({ name: "geolocation" })
      .then((result) => {
        if (cancelled) return;
        status = result;
        status.addEventListener("change", handleChange);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      status?.removeEventListener("change", handleChange);
    };
  }, []);

  useEffect(() => {
    if (!retryOnFocus) return undefined;

    const handleReturn = () => {
      if (document.visibilityState === "visible") {
        callbacksRef.current.onGranted?.();
      }
    };

    window.addEventListener("focus", handleReturn);
    document.addEventListener("visibilitychange", handleReturn);

    return () => {
      window.removeEventListener("focus", handleReturn);
      document.removeEventListener("visibilitychange", handleReturn);
    };
  }, [retryOnFocus]);
};

export default useGeolocationPermission;
